import { money } from './drawerFormat.js';
import { VARIANCE_ACTION_LABELS, VARIANCE_SOURCE_LABELS } from './varianceActions.js';

export const AUDIT_LINK_TOKEN_RE = /\[audit Link:([^\]]+)\]/i;
export const AUDIT_LINK_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const auditLinkToken = (actionId) => `[audit Link:${actionId}]`;

export const parseAuditLinkId = (text = '') => {
  const match = String(text || '').match(AUDIT_LINK_TOKEN_RE);
  return match?.[1] || '';
};

export const stripAuditLinkToken = (text = '') => String(text || '')
  .replace(AUDIT_LINK_TOKEN_RE, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

export const auditLinkDraftBody = (text = '') => {
  const value = String(text || '');
  if (/\n\[audit Link:[^\]]+\]/i.test(value)) {
    return value.replace(/\n\[audit Link:[^\]]+\]/i, '');
  }
  return value.replace(/\s?\[audit Link:[^\]]+\]/i, '');
};

export const withAuditLinkToken = (text = '', actionId = '') => {
  const body = auditLinkDraftBody(text);
  if (!actionId) return body;
  return body ? `${body}\n${auditLinkToken(actionId)}` : auditLinkToken(actionId);
};

const text = (value) => (value === null || value === undefined ? '' : String(value).trim());

const evidenceRowIdentity = (row = {}, index = 0) => text(
  row.sourceRowKey
  || row.targetRowKey
  || row.rowKey
  || row.storageKey
  || row.fileRowId
  || row.__rowKey
  || row.__sourceRowKey
  || row.rowNumber
  || row.lineNumber
  || row.invoiceLineNumber,
) || `row-${index}`;

const priceFor = (row = {}, keys = []) => {
  const value = keys.map((key) => row[key]).find((candidate) => candidate !== null && candidate !== undefined && candidate !== '');
  if (value === undefined) return '';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? money(numeric) : text(value);
};

export const evidenceOptionFromProduct = (source, row = {}, index = 0) => {
  const isCpq = source === 'cpq';
  const isAppSheet = source === 'appsheet';
  const productCode = text(row.productCode || row.oemProductCode || row.code);
  const productName = text(row.productName || row.productSelection || productCode || 'Product');
  const price = isCpq
    ? priceFor(row, ['budget', 'dealerCost', 'price'])
    : isAppSheet
      ? priceFor(row, ['mrr', 'price', 'dealerPrice'])
      : priceFor(row, ['dealerCost', 'price', 'budget']);
  const evidenceId = text(
    row.orderItemId
    || row.__orderItemId
    || row.id
    || row.referenceId
    || row.accountMatch?.orderItemId
    || row.accountMatch?.referenceId,
  );
  const date = text(row.effectiveDate || row.activationDate || row.orderStartDate || row.serviceMonth);
  const rowIdentity = evidenceRowIdentity(row, index).replace(/\s+/g, '-');
  return {
    id: `${source}-${evidenceId || productCode || 'uncoded'}-${rowIdentity}-${index}`,
    source,
    sourceLabel: source === 'gm' ? 'GM file' : source === 'cpq' ? 'CPQ file' : 'AppSheet',
    label: [productCode, productName].filter(Boolean).join(' - ') || `${source.toUpperCase()} row ${index + 1}`,
    productCode,
    productName,
    price,
    evidenceId,
    date,
    row,
  };
};

export const buildEvidenceOptions = ({ gmItems = [], cpqItems = [], appsheetData = null } = {}) => ({
  gm: (gmItems || []).map((item, index) => evidenceOptionFromProduct('gm', item, index)),
  cpq: (cpqItems || []).map((item, index) => evidenceOptionFromProduct('cpq', item, index)),
  appsheet: (appsheetData?.orderItems || []).map((item, index) => evidenceOptionFromProduct('appsheet', item, index)),
});

const firstSelected = (...items) => items.find(Boolean) || null;

const templateSourceTarget = (templateId, { gm, cpq, appsheet }) => {
  switch (templateId) {
    case 'add_to_gm':
      return {
        source: firstSelected(cpq, appsheet, gm),
        target: firstSelected(gm, cpq, appsheet),
      };
    case 'remove_from_gm':
      return {
        source: firstSelected(gm, cpq, appsheet),
        target: firstSelected(cpq, appsheet, gm),
      };
    case 'add_to_cpq':
      return {
        source: firstSelected(gm, appsheet, cpq),
        target: firstSelected(cpq, gm, appsheet),
      };
    case 'remove_from_cpq':
      return {
        source: firstSelected(cpq, gm, appsheet),
        target: firstSelected(gm, appsheet, cpq),
      };
    default:
      return {
        source: firstSelected(gm, cpq, appsheet),
        target: firstSelected(cpq, gm, appsheet),
      };
  }
};

export const templateContextFromSelections = ({ templateId = '', gm = null, cpq = null, appsheet = null, auditAction = null } = {}) => {
  const { source, target } = templateSourceTarget(templateId, { gm, cpq, appsheet });
  return {
    productCode: source?.productCode || target?.productCode || auditAction?.productCode,
    oldPrice: source?.price || auditAction?.price,
    newPrice: target?.price || auditAction?.proposedPrice,
    sourceLabel: source?.sourceLabel || auditAction?.sourceLabel || VARIANCE_SOURCE_LABELS[auditAction?.source],
    targetLabel: target?.sourceLabel || auditAction?.targetLabel || VARIANCE_SOURCE_LABELS[auditAction?.targetSource],
    evidenceId: source?.evidenceId || target?.evidenceId || auditAction?.id,
    orderItemId: source?.evidenceId || target?.evidenceId,
    auditActionId: auditAction?.id,
  };
};

export const actionLabel = (action = {}) => (
  action.actionLabel || VARIANCE_ACTION_LABELS[action.actionType] || action.actionType || 'Variance action'
);

export const actionRouteLabel = (action = {}) => {
  const sourceLabel = action.sourceLabel || VARIANCE_SOURCE_LABELS[action.source] || 'Source';
  const targetLabel = action.targetLabel || VARIANCE_SOURCE_LABELS[action.targetSource];
  return targetLabel ? `${sourceLabel} -> ${targetLabel}` : sourceLabel;
};

export const auditActionPreview = (action = {}) => ({
  title: actionLabel(action),
  route: actionRouteLabel(action),
  product: [action.productName || 'Product', action.productCode].filter(Boolean).join(' - '),
  price: action.price || action.price === 0 ? money(action.price) : '',
  proposedPrice: action.proposedPrice || action.proposedPrice === 0 ? money(action.proposedPrice) : '',
  problem: text(action.problem),
  fix: text(action.fix),
  note: text(action.note || action.metadata?.productNote?.text),
});

export const safeStorageGet = (key) => {
  try {
    return window.localStorage?.getItem(key) || '';
  } catch {
    return '';
  }
};

export const safeStorageSet = (key, value) => {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Local draft linkage is best-effort only.
  }
};

export const safeStorageRemove = (key) => {
  try {
    window.localStorage?.removeItem(key);
  } catch {
    // Local draft linkage is best-effort only.
  }
};

export const auditLinkDefaultBody = (action = {}) => {
  const product = action.productCode ? ` for ${action.productCode}` : '';
  return `Linked audit action: ${actionLabel(action)}${product}.`;
};

export const readStoredAuditLinkId = (storageKey, now = Date.now()) => {
  const raw = safeStorageGet(storageKey);
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return '';
    if (parsed.expiresAt && Number(parsed.expiresAt) <= now) {
      safeStorageRemove(storageKey);
      return '';
    }
    return text(parsed.id);
  } catch {
    return text(raw);
  }
};

export const writeStoredAuditLinkId = (storageKey, id, now = Date.now()) => {
  const value = text(id);
  if (!value) {
    safeStorageRemove(storageKey);
    return;
  }
  safeStorageSet(storageKey, JSON.stringify({
    id: value,
    expiresAt: now + AUDIT_LINK_STORAGE_TTL_MS,
  }));
};
